/* eslint-disable react-hooks/exhaustive-deps */
import { Line, Group } from 'react-konva';
import { useState, useRef, useEffect, useMemo } from 'react';
import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { useRefs } from 'contexts/RefContext';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import {
	degToRad, doesPolygonOverLap, generatePanelAt, generatePolygon, getRowSpacingGapPx,
	isCompletelyInsideFacet, Point, RectPoints, rotateVector
} from '../PanelUtil';
import { getExistingPanelsGroupID, KonvaGroupNames } from '../utils';
import { ORIENTATION } from 'store/slices/AddPanelModalSlice/types';
import { setAddSelectedMovablePanel, setDeletedPanels, setNewlyAddedPanel, setCurrentMovablePanels, setClearSelectedMovablePanels } from 'store/slices/PanelSlice';
import { PANEL_OFF_COLOR, SEMI_TRANPARENT_BLACK_PANEL } from '../../../constants';
import { getPanelPxScale } from '../../../util';

export default function CustomVectorPanel() {
	const { konvaRef: stageRef, movablePanelRef } = useRefs();

	const panelsGroup = useRef<Konva.Group | null>(null);
	const deletePanelsRef = useRef<{ panelId: string, roofSegmentId: string }[]>([]);
	const selectedPanelsRef = useRef<{ panelId: string, roofSegmentId: string }[]>([]);

	const scale = useKonvaImageScale();
	const dispatch = useDispatch();

	const { editModeEnabled, moveStage, rotateStage, deletedObstructionIDs, multiFingersPress, hideSatelliteOverlay } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { allRoofSegs, imagerySource, latLng, obstructions, groundMetersPerPixel } = useSelector((state: RootState) => state.roofData.data);

	const { orientation: panelOrientation } = useSelector((state: RootState) => state.addPanelModal.data);
	const { 
		enableMultipleAddPanel, deleteIndividualPanelMode, 
		enableAddPanel, enablePanelMove,
		currentMovablePanels, isInvalidPanelOperation,
		selectedMovablePanels
	} = useSelector((state: RootState) => state.panelSlice.data);
	const [facetPolygon, setFacetPolygon] = useState<number[]>([]);
	const [basePanelDetails, setBasePanelDetails] = useState<{
		facetId: string;
		width: number;
		height: number;
		stepX: number[];
		stepY: number[];
		rotationAngle: number;
		origin: Point,
		overlaped: boolean,
		orientaion: string,
	} | null>(null);

	const [panels, setPanels] = useState<Array<{ coordinates: RectPoints, valid: boolean }>>([]);

	const [deleteRect, setDeleteRect] = useState<{
		x1: number;
		y1: number;
		x2: number;
		y2: number;
	} | null>(null);

	const pxScale = getPanelPxScale(groundMetersPerPixel);

	const scaledObs = useMemo(() => {
		return obstructions.filter(eachObj => !deletedObstructionIDs.includes(eachObj.id)).map(eachObs => {
			return generatePolygon(eachObs.coordinates.flat().map((point, index) => index % 2 === 0 ? point * scale.x : point * scale.y));
		});
	}, [scale, deletedObstructionIDs]);

	const handlePointerDown = (e: KonvaEventObject<PointerEvent>) => {
		const stage = stageRef?.current;
		const pointerPos = stage?.getPointerPosition();
		if (!pointerPos) return;
		
		const shape = stage?.getIntersection(pointerPos);
		const isPanelShape = shape?.attrs?.name?.includes('panel');
		if(isPanelShape) {
			return;
		}
		if(isInvalidPanelOperation) return;
		if(!isPanelShape) {
			dispatch(setClearSelectedMovablePanels());
		}
		if (
			!(enableAddPanel || deleteIndividualPanelMode || enablePanelMove) ||
			!stage ||
			moveStage ||
			rotateStage) return;

		const pos = stage.getRelativePointerPosition();
		const pointerPosition = stage.getPointerPosition();

		if(!pos) return;
		const { x, y } = pos;

		if (deleteIndividualPanelMode || enablePanelMove) {
			setDeleteRect({ x1: x, y1: y, x2: x, y2: y });
			return;
		}

		const targetFacetPolygon = stage.getAllIntersections(pointerPosition).find((node) => node.hasName(`${KonvaGroupNames.existingFacet}`));

		if (enableMultipleAddPanel && targetFacetPolygon) {
			const matchingRoofSeg = allRoofSegs.find((seg: any) => seg.id === targetFacetPolygon.id());
			if (matchingRoofSeg) {
				const prebuildLineRects = stage.find(`.panel-${matchingRoofSeg.id}`);
				const currentCoords = matchingRoofSeg.setbackPolygonCoords.length > 0 ? matchingRoofSeg.setbackPolygonCoords : matchingRoofSeg.simplifiedHullCoords;
				const scaledCoords = currentCoords
					.map(coordPair => {
						let [x, y] = coordPair;
						x *= scale.x;
						y *= scale.y;
						return [x, y];
					})
					.flat();
				setFacetPolygon(scaledCoords);
				let panelWidth = (matchingRoofSeg.panelWidth);
				let panelHeight = (matchingRoofSeg.panelHeight);
				let rotationAngle = (matchingRoofSeg.azimuthDegrees) % 360;
				rotationAngle = degToRad(rotationAngle);
				if (panelOrientation === ORIENTATION.LANDSCAPE && panelHeight > panelWidth) {
					[panelWidth, panelHeight] = [panelHeight, panelWidth];
				}
				if (panelOrientation === ORIENTATION.PORTRAIT && panelWidth > panelHeight) {
					[panelWidth, panelHeight] = [panelHeight, panelWidth];
				}
				const scaledPanelWidth = (panelWidth / pxScale) * scale.x;
				const scaledPanelHeight = (panelHeight / pxScale) * Math.cos(matchingRoofSeg.pitchDegrees * (Math.PI / 180)) * scale.y;

				const rowSpacingGapPx = getRowSpacingGapPx(matchingRoofSeg, pxScale, scale.y);

				const basePanel = generatePanelAt(x, y, scaledPanelWidth, scaledPanelHeight, rotationAngle);
				const basepPanelPolygon = generatePolygon(basePanel);
				const intersectedPolygon = prebuildLineRects.find(eachPanel => {
					const convertedPolygon = generatePolygon(eachPanel.attrs.points);
					return doesPolygonOverLap(basepPanelPolygon, convertedPolygon);
				});
				if (!isCompletelyInsideFacet(basePanel, scaledCoords, 0.50)) return;
				if (intersectedPolygon) {
					const customPanelOrientaion = intersectedPolygon.attrs.orientation;
					const matchingPanel = matchingRoofSeg.panels.find(eachPanel => eachPanel.id === intersectedPolygon.attrs.id);
					let intersectedPanelOrientation = matchingPanel?.orientation || customPanelOrientaion;
					intersectedPanelOrientation = intersectedPanelOrientation === ORIENTATION.PORTRAIT ? 'POTRAITT' : intersectedPanelOrientation;

					switch (panelOrientation) {
					case ORIENTATION.LANDSCAPE:
						console.log('from landscape');
						if (intersectedPanelOrientation !== ORIENTATION.LANDSCAPE) {
							[panelWidth, panelHeight] = [panelHeight, panelWidth];
						}
						break;
					case ORIENTATION.PORTRAIT:
						console.log('from portrait', intersectedPanelOrientation, ORIENTATION.PORTRAIT);
						if ((intersectedPanelOrientation !== ORIENTATION.PORTRAIT) && !(intersectedPanelOrientation === 'POTRAITT' && ORIENTATION.PORTRAIT === 'PORTRAIT')) {
							[panelWidth, panelHeight] = [panelHeight, panelWidth];
						}
						break;
					case ORIENTATION.DEFAULT:
						console.log('from default', );
						if (intersectedPanelOrientation !== matchingRoofSeg.orientation) {
							[panelWidth, panelHeight] = [panelHeight, panelWidth];
						}
						break;
					}

					const scaledPanelWidth = (panelWidth / pxScale) * scale.x;
					const scaledPanelHeight = (panelHeight / pxScale) * Math.cos(matchingRoofSeg.pitchDegrees * (Math.PI / 180)) * scale.y;

					const points = intersectedPolygon.attrs.points;
					const centerX = (points[0] + points[2] + points[4] + points[6]) / 4;
					const centerY = (points[1] + points[3] + points[5] + points[7]) / 4;

					setBasePanelDetails({
						facetId: matchingRoofSeg.id,
						width: scaledPanelWidth,
						height: scaledPanelHeight,
						rotationAngle,
						stepX: rotateVector([scaledPanelWidth, 0], rotationAngle),
						stepY: rotateVector([0, scaledPanelHeight + rowSpacingGapPx], rotationAngle),
						origin: [centerX, centerY],
						overlaped: true,
						orientaion: intersectedPanelOrientation
					});
					setPanels([]);
				} else {
					let newOrientation = panelOrientation === ORIENTATION.DEFAULT ? matchingRoofSeg.orientation : panelOrientation;
					newOrientation = newOrientation === ORIENTATION.PORTRAIT ? 'POTRAITT' : newOrientation;
					const isValidPanel = isCompletelyInsideFacet(basePanel, scaledCoords, 0.98);
					const basePanelPolygon = generatePolygon(basePanel);
					const hasCollisionWithObstructions = scaledObs.some(eachObs => {
						return doesPolygonOverLap(basePanelPolygon, eachObs);
					});
					setPanels([{ coordinates: basePanel, valid: isValidPanel && !hasCollisionWithObstructions }]);
					setBasePanelDetails({
						facetId: matchingRoofSeg.id,
						width: scaledPanelWidth,
						height: scaledPanelHeight,
						rotationAngle,
						stepX: rotateVector([scaledPanelWidth, 0], rotationAngle),
						stepY: rotateVector([0, scaledPanelHeight + rowSpacingGapPx], rotationAngle),
						origin: [x, y],
						overlaped: false,
						orientaion: newOrientation
					});
				}
			}
		} else {
			// Rotation-safe: inverting the full absolute transform (not just translate+scale)
			// keeps the panel under the cursor once the stage itself is rotated.
			const adjustedPointerPosition = stage.getRelativePointerPosition();
			if (!adjustedPointerPosition) return;

			movablePanelRef?.current?.visible(true);
			movablePanelRef?.current?.position(adjustedPointerPosition);
			movablePanelRef?.current?.startDrag();
		}
	};

	const handlePointerMove = (e: KonvaEventObject<PointerEvent>) => {
		// map only: the delete/move rect-select and the multi-panel grid preview are both
		// purely state-driven off this handler — freeze both by simply not updating that
		// state while hidden, so they sit exactly as left and resume on the next real move.
		if (hideSatelliteOverlay) return;
		const pos = e.target.getStage()?.getRelativePointerPosition();
		const stage = stageRef?.current;
		if (!pos || !stage || ((deleteIndividualPanelMode || enablePanelMove) && !deleteRect)) return;
		const { x, y } = pos;

		if (deleteIndividualPanelMode || enablePanelMove) {
			if (deleteRect) {
				const rectPoints = [
					deleteRect.x1, deleteRect.y1,
					x, deleteRect.y1,
					x, y,
					deleteRect.x1, y
				];
				const prebuildLineRects = stage
					.find('Line')
					.filter((line) => line.name().startsWith('panel-'));

				const tempPanels = prebuildLineRects.reduce((acc: any, eachPanel: any) => {
					const convertedPolygon = generatePolygon(eachPanel.attrs.points);
					const rectPolygon = generatePolygon(rectPoints);
					const isIntersecting = doesPolygonOverLap(rectPolygon, convertedPolygon);
					if (isIntersecting) {
						acc.push({ panelId: eachPanel.attrs.id, roofSegmentId: eachPanel.attrs.name.replace('panel-', '') });
						eachPanel.fill('rgba(0, 128, 255, 0.5)');
					} else {
						eachPanel.fill(SEMI_TRANPARENT_BLACK_PANEL);
					}
					return acc;
				}, []);
				if (deleteIndividualPanelMode) {
					deletePanelsRef.current = tempPanels;
				} else {
					selectedPanelsRef.current = tempPanels;
				}
				setDeleteRect((prev) => prev ? { ...prev, x2: x, y2: y } : null);
				return;
			}
		}

		if (!basePanelDetails) return;

		const dx = x - basePanelDetails.origin[0];
		const dy = y - basePanelDetails.origin[1];

		const projX =
			(dx * basePanelDetails.stepX[0] + dy * basePanelDetails.stepX[1]) / (basePanelDetails.stepX[0] ** 2 + basePanelDetails.stepX[1] ** 2);
		const projY =
			(dx * basePanelDetails.stepY[0] + dy * basePanelDetails.stepY[1]) / (basePanelDetails.stepY[0] ** 2 + basePanelDetails.stepY[1] ** 2);

		const maxStepsX = Math.round(projX);
		const maxStepsY = Math.round(projY);

		const newPanels = [];

		const prebuildLineRects = stage.find(`.panel-${basePanelDetails.facetId}`);

		for (let i = Math.min(0, maxStepsX); i <= Math.max(0, maxStepsX); i++) {
			for (let j = Math.min(0, maxStepsY); j <= Math.max(0, maxStepsY); j++) {
				if (basePanelDetails.overlaped && i == 0 && j == 0) continue;
				const px = basePanelDetails.origin[0] + i * basePanelDetails.stepX[0] + j * basePanelDetails.stepY[0];
				const py = basePanelDetails.origin[1] + i * basePanelDetails.stepX[1] + j * basePanelDetails.stepY[1];

				const rect = generatePanelAt(px, py, basePanelDetails.width, basePanelDetails.height, basePanelDetails.rotationAngle);

				const smallRectPolygon = generatePolygon(rect);

				const hasCollision = prebuildLineRects.some(eachPanel => {
					const convertedPolygon = generatePolygon(eachPanel.attrs.points);
					return doesPolygonOverLap(smallRectPolygon, convertedPolygon);
				});

				const hasCollisionWithObstructions = scaledObs.some(eachObs => {
					return doesPolygonOverLap(smallRectPolygon, eachObs);
				});

				// const hasCompleteColloid = prebuildLineRects.some(eachPanel => {
				// 	return isCompletelyInsideFacet(rect, eachPanel.attrs.points, 0.99);
				// });

				const isInside = isCompletelyInsideFacet(rect, facetPolygon, 0.50);
				if (isInside) {
					const isInsideThreshold = isCompletelyInsideFacet(rect, facetPolygon, 0.98);
					if (isInsideThreshold && !hasCollision && !hasCollisionWithObstructions) {
						newPanels.push({ coordinates: rect, valid: true });
					} else {
						newPanels.push({ coordinates: rect, valid: false });
					}
				}
			}
		}
		setPanels(newPanels);
	};

	const handlePointerUp = () => {
		if (!isInvalidPanelOperation && selectedMovablePanels.length > 0) {
			const movedPanels = selectedMovablePanels.map(eachPanel => ({
				orientation: eachPanel.orientation,
				originSegmentId: eachPanel.originSegmentId,
				exteriorCoords: eachPanel.exteriorCoords,
				panelId: eachPanel.id,
				roofSegmentId: eachPanel.segmentId
			}));
			dispatch(setCurrentMovablePanels({ currentMovablePanelArr: movedPanels, updateHistory: false }));
			selectedPanelsRef.current = [];
			setDeleteRect(null);
			setPanels([]);
			setBasePanelDetails(null);
			setFacetPolygon([]);
			return;
		}

		if (deleteIndividualPanelMode) {
			if (deletePanelsRef.current.length > 0) {
				dispatch(setDeletedPanels({ data: deletePanelsRef.current }));
			}
			deletePanelsRef.current = [];
			setDeleteRect(null);
			return;
		}


		if (enablePanelMove) {
			const selectedPanels = selectedPanelsRef.current
				.map(({ panelId, roofSegmentId }) => {
					const roofSeg = allRoofSegs.find((seg: any) => seg.id === roofSegmentId);
					if (!roofSeg) return null;
					return roofSeg.panels.find((p: any) => p.id === panelId);
				})
				.filter((panel): panel is RasterSolarPanel => panel !== null && panel !== undefined);
			if (selectedPanels.length > 0) {
				dispatch(setAddSelectedMovablePanel(selectedPanels));
			}

			selectedPanelsRef.current = [];
			setDeleteRect(null);
			return;
		}

		if (!basePanelDetails) return;

		const newlyAddedPanels = panels.filter(eachPanel => eachPanel.valid).map((eachPanel) => {
			const exteriorCoords = [];
			for (let i = 0; i < eachPanel.coordinates.length - 1; i += 2) {
				exteriorCoords.push([eachPanel.coordinates[i] / scale.x, eachPanel.coordinates[i + 1] / scale.y]);
			}
			return {
				exteriorCoords,
				orientation: basePanelDetails.orientaion === 'POTRAITT' ? 'PORTRAIT' : basePanelDetails.orientaion,
				roofSegmentId: basePanelDetails.facetId,
			};
		});
		setPanels([]);
		setBasePanelDetails(null);
		setFacetPolygon([]);

		if (enableMultipleAddPanel && newlyAddedPanels.length > 0) {
			dispatch(setNewlyAddedPanel({ addedPanels: newlyAddedPanels }));
		}

		if (movablePanelRef?.current) {
			movablePanelRef.current.stopDrag();
		}
	};

	useEffect(() => {
		if (!stageRef?.current || moveStage || rotateStage) return;
		if(multiFingersPress) {
			return;
		}

		const stage = stageRef.current;
		// at 0, finger jitter on a tap starts a stage drag, and Konva cancels the tap (e.g. drawing a facet point)
		Konva.dragDistance = 8;
		Konva.hitOnDragEnabled = true;
		stage.container().style.touchAction = 'none';

		stage.on('pointerdown', handlePointerDown);
		stage.on('pointermove', handlePointerMove);
		stage.on('pointerup', handlePointerUp);

		return () => {
			stage.off('pointerdown', handlePointerDown);
			stage.off('pointermove', handlePointerMove);
			stage.off('pointerup', handlePointerUp);
		};
	}, [
		stageRef, handlePointerDown, handlePointerMove, handlePointerUp,
		enableAddPanel, editModeEnabled, panelOrientation,
		moveStage, rotateStage, deleteIndividualPanelMode, enableMultipleAddPanel,
		enablePanelMove, currentMovablePanels, isInvalidPanelOperation,
		selectedMovablePanels, multiFingersPress
	]);

	return (
		<Group ref={panelsGroup}>
			{panels.map((eachPanel, i) => (
				<Line
					key={i}
					points={eachPanel.coordinates}
					stroke={eachPanel.valid ? 'rgba(0, 128, 255, 0.8)' : 'red'}
					strokeWidth={1.4}
					closed={true}
					listening={false}
					strokeScaleEnabled={false}
					fill={PANEL_OFF_COLOR}
				/>
			))}

			{deleteRect && (
				<Line
					name={'delete-rect'}
					points={[
						deleteRect.x1,
						deleteRect.y1,
						deleteRect.x2,
						deleteRect.y1,
						deleteRect.x2,
						deleteRect.y2,
						deleteRect.x1,
						deleteRect.y2,
					]}
					fill={'rgba(0, 128, 255, 0.15)'}
					stroke={'rgba(0, 128, 255, 0.5)'}
					strokeWidth={1.4}
					closed
					listening={false}
					strokeScaleEnabled={false}
				/>
			)}
		</Group>
	);
}